import {createSlice, ActionReducerMapBuilder, current} from '@reduxjs/toolkit'
import {
  AssignedPolicyTypes,
  Policy,
  SliceStatus,
  UnknownObject,
  SSLInspectionStatus,
} from 'interfaces'
import {
  updatePolicyUpdateField,
  resetPolicyUpdateField,
  formSubmitted,
  savePressed,
  updatePolicyName,
  setSSLInspectionData,
  setSSLInspectionSnapshot,
  setSSLInspectionBasePolicy,
} from './policiesActions'

import {
  getPolicyAssignee,
  getPolicyAssigneeFailure,
  getPolicyAssigneeSuccess,
  assignPolicy,
  assignPolicyFailure,
  assignPolicySuccess,
  changeAssignedPolicy,
  removeAssignedPolicySnapshot,
  setAssignedUsersGroupsToDelete,
  getPolicyClashGroups,
  getPolicyClashGroupsFailure,
  getPolicyClashGroupsSuccess,
  resetPolicyClash,
  resetPolicyUserList,
} from './Users/usersActions'

import {
  getCloudApp,
  getCloudAppSuccess,
  getCloudAppFailure,
  updateCloudApp,
  updateCloudAppSuccess,
  updateCloudAppFailure,
  getBasePolicyCloudApp,
  getBasePolicyCloudAppSuccess,
  getBasePolicyCloudAppFailure,
  changeCloudApp,
  removeCloudAppSnapshot,
  updateCacAdditionalControls,
} from './CloudApp/cloudAppActions'
import {hasCloudAppUnsavedChanges} from './CloudApp/cloudApp.utils'
import {isEqual} from 'lodash'
import {
  CloudAppResponseType,
  PolicyClashDataInterface,
  PolicyUpdateField,
} from 'interfaces/Policy'
import {isEmptyObject} from 'utils'
import {box, defaultCloudApplication, salesforce} from './policyHelpers'

type PoliciesSliceState = {
  sslInspectionState: {
    data: SSLInspectionStatus
    snapshot: SSLInspectionStatus
    basePolicy: SSLInspectionStatus
  }
  updatePolicyStatus: SliceStatus
  selectedPolicyName: string

  policyAssignee: {
    data: AssignedPolicyTypes[]
    snapshot: AssignedPolicyTypes[]
    status: SliceStatus
    delete_groups: string[]
    delete_users: string[]
  }

  cloudApp: {
    data: CloudAppResponseType
    snapshot: CloudAppResponseType
    /** Set only by loads; saves and edits never touch it. */
    loadStatus: SliceStatus
    snapshotRevision: number
    /** The one Cloud App save in flight, if any. */
    savingPolicyName?: string
    /** The policy that `data` and `snapshot` belong to. */
    draftPolicyName?: string
    resetCloudStatus?: SliceStatus
    baseCloudApp?: CloudAppResponseType
  }

  policyClashGroups: PolicyClashDataInterface
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  policyClashLastEvaluatedKey: any
  policyClashGroupsStatus: SliceStatus

  status: SliceStatus
  error: string
  updatedPolicyField?: PolicyUpdateField
}

/** Edits wait for the policy's data and are blocked while that policy saves. */
export const isCloudAppLocked = (state: PoliciesSliceState): boolean =>
  state.cloudApp.savingPolicyName === state.selectedPolicyName ||
  Object.keys(state.cloudApp.snapshot).length === 0

const policiesSlice = createSlice({
  name: 'policies',
  initialState: {
    sslInspectionState: {
      data: SSLInspectionStatus.ENABLED,
      snapshot: SSLInspectionStatus.ENABLED,
      basePolicy: SSLInspectionStatus.ENABLED,
    },
    updatePolicyStatus: SliceStatus.idle,

    policyAssignee: {
      data: [] as AssignedPolicyTypes[],
      snapshot: [] as AssignedPolicyTypes[],
      status: SliceStatus.idle,
      delete_groups: [],
      delete_users: [],
      add_users: [],
      add_groups: [],
    },

    cloudApp: {
      data: {} as CloudAppResponseType,
      snapshot: {} as CloudAppResponseType,
      loadStatus: SliceStatus.idle,
      snapshotRevision: 0,
      savingPolicyName: undefined as string | undefined,
      draftPolicyName: undefined as string | undefined,
      resetCloudStatus: SliceStatus.idle,
      baseCloudApp: {} as CloudAppResponseType,
    },

    status: SliceStatus.idle,
    error: '',
    selectedPolicyName: (localStorage.getItem('selectedPolicyName')
      ? localStorage.getItem('selectedPolicyName')
      : Policy.BasePolicy) as string,
    updatedPolicyField: {} as PolicyUpdateField,

    policyClashGroups: {
      participating_groups: [],
      grouped_clashes: [],
    } as PolicyClashDataInterface,
    policyClashLastEvaluatedKey: {} as UnknownObject,
    policyClashGroupsStatus: SliceStatus.idle,
    policyClashGroupsHasMoreRecord: false,
    policyReverted: false,
    formSubmitted: false,
    savePressed: false,
  },
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<PoliciesSliceState>) =>
    builder
      .addCase(updatePolicyName, (state, action) => ({
        ...state,
        selectedPolicyName: action.payload,
      }))
      .addCase(getPolicyAssignee, state => ({
        ...state,
        policyAssignee: {...state.policyAssignee, status: SliceStatus.pending},
      }))
      .addCase(getPolicyAssigneeFailure, state => ({
        ...state,
        policyAssignee: {...state.policyAssignee, status: SliceStatus.rejected},
      }))
      .addCase(getPolicyAssigneeSuccess, (state, action) => ({
        ...state,
        policyAssignee: {
          ...state.policyAssignee,
          data: action.payload,
          snapshot: action.payload,
          status: SliceStatus.resolved,
        },
      }))
      .addCase(assignPolicy, state => ({
        ...state,
        updatePolicyStatus: SliceStatus.pending,
      }))
      .addCase(assignPolicyFailure, state => ({
        ...state,
        updatePolicyStatus: SliceStatus.rejected,
      }))
      .addCase(assignPolicySuccess, state => ({
        ...state,
        updatePolicyStatus: SliceStatus.resolved,
        policyAssignee: {
          ...state.policyAssignee,
          // reset delete groups and user on success
          delete_groups: [],
          delete_users: [],
          snapshot: state.policyAssignee.data,
        },
      }))
      .addCase(changeAssignedPolicy, (state, action) => ({
        ...state,
        policyAssignee: {
          ...state.policyAssignee,
          data: action.payload,
          ...determineAddedUsersOrGroups(
            current(state.policyAssignee.snapshot),
            action.payload,
          ),
        },
      }))
      .addCase(removeAssignedPolicySnapshot, state =>
        !isEqual(state.policyAssignee.data, state.policyAssignee.snapshot) ||
        state.policyAssignee.delete_groups.length > 0 ||
        state.policyAssignee.delete_users.length > 0
          ? {
              ...state,
              policyAssignee: {
                ...state.policyAssignee,
                data: state.policyAssignee.snapshot,
                delete_groups: [],
                delete_users: [],
                // in this case, original and updated state is same, so no need to determine added users or groups
                add_users: [],
                add_groups: [],
              },
            }
          : {
              ...state,
            },
      )
      .addCase(resetPolicyUserList, state => ({
        ...state,
        policyAssignee: {
          data: [] as AssignedPolicyTypes[],
          snapshot: [] as AssignedPolicyTypes[],
          status: SliceStatus.idle,
          delete_groups: [],
          delete_users: [],
          add_users: [],
          add_groups: [],
        },
      }))
      .addCase(setAssignedUsersGroupsToDelete, (state, action) => ({
        ...state,
        policyAssignee: {
          ...state.policyAssignee,
          [action.payload.type]: [
            ...state.policyAssignee[action.payload.type],
            action.payload.value,
          ],
        },
      }))
      .addCase(getCloudApp, (state, {payload: {policyName}}) => {
        state.cloudApp.loadStatus = SliceStatus.pending
        if (state.cloudApp.draftPolicyName === policyName) return
        // Another policy's draft and base must never be saved for this one.
        state.cloudApp.draftPolicyName = policyName
        state.cloudApp.data = {} as CloudAppResponseType
        state.cloudApp.snapshot = {} as CloudAppResponseType
        state.cloudApp.baseCloudApp = undefined
      })
      .addCase(getCloudAppFailure, state => {
        state.cloudApp.loadStatus = SliceStatus.rejected
      })
      .addCase(getCloudAppSuccess, (state, {payload}) => {
        const {data, snapshot} = state.cloudApp
        state.cloudApp.loadStatus = SliceStatus.resolved
        // Last write wins: unsaved edits are kept over a later load.
        if (hasCloudAppUnsavedChanges(data, snapshot)) return

        const fresh = modifyCloudAppData(payload)
        state.cloudApp.data = cloneCloudAppData(fresh)
        state.cloudApp.snapshot = cloneCloudAppData(fresh)
        // The base copy belongs to the draft it was fetched for.
        state.cloudApp.baseCloudApp = undefined
        state.cloudApp.snapshotRevision += 1
      })
      .addCase(getBasePolicyCloudApp, state => ({
        ...state,
        cloudApp: {
          ...state.cloudApp,
          resetCloudStatus: SliceStatus.pending,
        },
      }))
      .addCase(getBasePolicyCloudAppFailure, state => ({
        ...state,
        cloudApp: {
          ...state.cloudApp,
          resetCloudStatus: SliceStatus.rejected,
        },
      }))
      .addCase(getBasePolicyCloudAppSuccess, (state, {payload}) => {
        state.cloudApp.resetCloudStatus = SliceStatus.resolved
        state.cloudApp.baseCloudApp = payload
        if (isCloudAppLocked(state)) return
        state.cloudApp.data = {inheritsCAC: true, cac: payload.cac}
      })
      .addCase(changeCloudApp, (state, {payload}) => {
        if (isCloudAppLocked(state)) return
        state.cloudApp.data = payload
      })
      .addCase(
        updateCacAdditionalControls,
        (state, {payload: {appId, changes}}) => {
          if (isCloudAppLocked(state)) return
          const app = state.cloudApp.data.cac?.[appId]
          if (!app) return
          Object.assign(app.additional_controls, changes)
          state.cloudApp.data.inheritsCAC = false
        },
      )
      .addCase(updateCloudApp, (state, {payload}) =>
        // One save at a time; the save epic ignores this action too.
        state.cloudApp.savingPolicyName !== undefined
          ? state
          : {
              ...state,
              cloudApp: {
                ...state.cloudApp,
                savingPolicyName: payload.request.policyName,
              },
              status: SliceStatus.pending,
              savePressed: false,
              formSubmitted: false,
            },
      )
      .addCase(updateCloudAppFailure, (state, {payload: {policyName}}) => {
        // Only one save runs at a time, so any other result is stale.
        if (state.cloudApp.savingPolicyName !== policyName) return
        state.cloudApp.savingPolicyName = undefined
        state.status =
          policyName === state.selectedPolicyName
            ? SliceStatus.rejected
            : SliceStatus.resolved
      })
      .addCase(
        updateCloudAppSuccess,
        (state, {payload: {policyName, saved}}) => {
          if (state.cloudApp.savingPolicyName !== policyName) return
          state.cloudApp.savingPolicyName = undefined
          state.status = SliceStatus.resolved
          // A draft with no owner belongs to the selected policy. Once a load
          // names an owner, only that policy's save may replace the draft.
          const draftOwner =
            state.cloudApp.draftPolicyName ?? state.selectedPolicyName
          if (policyName !== draftOwner) return

          state.cloudApp.data = cloneCloudAppData(saved)
          state.cloudApp.snapshot = cloneCloudAppData(saved)
          state.cloudApp.snapshotRevision += 1
        },
      )
      .addCase(removeCloudAppSnapshot, state => ({
        ...state,
        cloudApp: {
          ...state.cloudApp,
          data: cloneCloudAppData(state.cloudApp.snapshot),
          snapshotRevision: state.cloudApp.snapshotRevision + 1,
        },
        policyReverted: true,
      }))
      .addCase(updatePolicyUpdateField, (state, action) => ({
        ...state,
        updatedPolicyField: {
          ...state.updatedPolicyField,
          ...action.payload,
        },
      }))
      .addCase(resetPolicyUpdateField, state => ({
        ...state,
        updatedPolicyField: {
          value1: '',
          value2: '',
          throwError: false,
          throwError1: false,
          throwError2: false,
        },
        policyReverted: false,
        savePressed: false,
      }))
      .addCase(formSubmitted, (state, action) => ({
        ...state,
        formSubmitted: action.payload,
      }))
      .addCase(savePressed, (state, action) => ({
        ...state,
        savePressed: action.payload,
      }))
      .addCase(getPolicyClashGroups, state => ({
        ...state,
        policyClashGroupsStatus: SliceStatus.pending,
      }))
      .addCase(getPolicyClashGroupsFailure, state => ({
        ...state,
        policyClashGroupsStatus: SliceStatus.rejected,
      }))
      .addCase(getPolicyClashGroupsSuccess, (state, action) => ({
        ...state,
        policyClashGroups: {
          ...state?.policyClashGroups,
          ...{
            participating_groups: [
              ...(state.policyClashGroups?.participating_groups ?? []),
              ...(action?.payload?.data?.participating_groups || []),
            ],
            grouped_clashes: [
              ...(state.policyClashGroups?.grouped_clashes ?? []),
              ...(action?.payload?.data?.grouped_clashes || []),
            ],
          },
        },
        policyClashLastEvaluatedKey: action.payload.LastEvaluatedKey,
        policyClashGroupsStatus: SliceStatus.resolved,
        policyClashGroupsHasMoreRecord:
          action.payload &&
          'LastEvaluatedKey' in action.payload &&
          action.payload.LastEvaluatedKey &&
          !isEmptyObject(action.payload.LastEvaluatedKey),
      }))

      .addCase(resetPolicyClash, state => ({
        ...state,
        policyClashGroups: {
          participating_groups: [],
          grouped_clashes: [],
        } as PolicyClashDataInterface,
        policyClashLastEvaluatedKey: {} as UnknownObject,
        policyClashGroupsHasMoreRecord: false,
      }))
      // Set SSL Inspection state in data
      .addCase(setSSLInspectionData, (state, action) => ({
        ...state,
        sslInspectionState: {
          ...state.sslInspectionState,
          data: action.payload,
        },
      }))
      .addCase(setSSLInspectionSnapshot, (state, action) => ({
        ...state,
        sslInspectionState: {
          ...state.sslInspectionState,
          snapshot: action.payload,
        },
      }))
      .addCase(setSSLInspectionBasePolicy, (state, action) => ({
        ...state,
        sslInspectionState: {
          ...state.sslInspectionState,
          basePolicy: action.payload,
        },
      }))
      .addDefaultCase(state => state),
})

// Only for cloudapp (salesforce and box)
// add 'defaultcloudapplication' as blockpage if api response for cac.blockpage is empty
const modifyCloudAppData = (
  data: CloudAppResponseType,
): CloudAppResponseType => {
  let updatedData = {...data}
  if (data?.cac) {
    let updatedCac = {...data?.cac}
    Object.entries(updatedCac).forEach(([key, value]) => {
      if (key === salesforce || key === box) {
        updatedCac = {
          ...updatedCac,
          [key]: {
            ...value,
            blockPage: value?.blockPage || defaultCloudApplication,
          },
        }
      }
    })

    updatedData = {...updatedData, cac: updatedCac}
  }
  return {...updatedData}
}

const cloneCloudAppData = (data: CloudAppResponseType): CloudAppResponseType =>
  JSON.parse(JSON.stringify(data)) as CloudAppResponseType

export const {reducer: policiesReducer, name: policiesReducerName} =
  policiesSlice

export type TPoliciesActions =
  | ReturnType<typeof updatePolicyName>
  | ReturnType<typeof getPolicyAssignee>
  | ReturnType<typeof getPolicyAssigneeFailure>
  | ReturnType<typeof getPolicyAssigneeSuccess>
  | ReturnType<typeof assignPolicy>
  | ReturnType<typeof assignPolicyFailure>
  | ReturnType<typeof assignPolicySuccess>
  | ReturnType<typeof changeAssignedPolicy>
  | ReturnType<typeof removeAssignedPolicySnapshot>
  | ReturnType<typeof setAssignedUsersGroupsToDelete>
  | ReturnType<typeof getCloudApp>
  | ReturnType<typeof getCloudAppSuccess>
  | ReturnType<typeof getCloudAppFailure>
  | ReturnType<typeof changeCloudApp>
  | ReturnType<typeof updateCacAdditionalControls>
  | ReturnType<typeof removeCloudAppSnapshot>
  | ReturnType<typeof updateCloudApp>
  | ReturnType<typeof updateCloudAppSuccess>
  | ReturnType<typeof getBasePolicyCloudApp>
  | ReturnType<typeof getBasePolicyCloudAppSuccess>
  | ReturnType<typeof getBasePolicyCloudAppFailure>
  | ReturnType<typeof updatePolicyUpdateField>
  | ReturnType<typeof resetPolicyUpdateField>
  | ReturnType<typeof formSubmitted>
  | ReturnType<typeof savePressed>
  | ReturnType<typeof getPolicyClashGroups>
  | ReturnType<typeof getPolicyClashGroupsFailure>
  | ReturnType<typeof getPolicyClashGroupsSuccess>
  | ReturnType<typeof resetPolicyClash>
  | ReturnType<typeof resetPolicyUserList>

export const policiesActions = {
  getPolicyAssignee,
  getPolicyAssigneeFailure,
  getPolicyAssigneeSuccess,
  assignPolicy,
  assignPolicyFailure,
  assignPolicySuccess,
  changeAssignedPolicy,
  removeAssignedPolicySnapshot,
  setAssignedUsersGroupsToDelete,
  getCloudApp,
  getCloudAppSuccess,
  getCloudAppFailure,
  changeCloudApp,
  updateCacAdditionalControls,
  removeCloudAppSnapshot,
  updateCloudApp,
  updateCloudAppSuccess,
  updateCloudAppFailure,
  getBasePolicyCloudApp,
  getBasePolicyCloudAppSuccess,
  getBasePolicyCloudAppFailure,
  updatePolicyName,
  updatePolicyUpdateField,
  resetPolicyUpdateField,
  formSubmitted,
  savePressed,
  getPolicyClashGroups,
  getPolicyClashGroupsFailure,
  getPolicyClashGroupsSuccess,
  resetPolicyClash,
  resetPolicyUserList,
}

export type PoliciesState = ReturnType<typeof policiesReducer>

interface AddUsersOrGroups {
  add_users: string[]
  add_groups: string[]
}
/**
 * Determines the users or groups that have been added by comparing the original state with the updated state.
 *
 * @param originalState - The original list of assigned policy users and group.
 * @param updatedState - The updated list of assigned policy users and group.
 * @returns An object containing the users and groups that have been added.
 */
const determineAddedUsersOrGroups = (
  originalState: AssignedPolicyTypes[],
  updatedState: AssignedPolicyTypes[],
): AddUsersOrGroups => {
  const users = updatedState.filter(
    d =>
      d.type === 'users' && !originalState.some(o => o.emailId === d.emailId),
  )
  const groups = updatedState.filter(
    d =>
      d.type === 'groups' && !originalState.some(o => o.emailId === d.emailId),
  )
  return {
    add_users: users.map(u => u.emailId),
    add_groups: groups.map(g => g.emailId),
  }
}
